import { CommonModule, NgOptimizedImage } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Directives
// Material Modules
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { RouterModule } from '@angular/router';
import { BlockChatComponent } from '@core-app/blocks/block-chat/block-chat.component';
import { BlockProfileComponent } from '@core-app/blocks/block-profile/block-profile.component';
import { BlockLogoSliderComponent } from '@core-app/blocks/block-logo-slider/block-logo-slider.component';
import { BlockCallToActionComponent } from '@core-app/blocks/block-call-to-action/block-call-to-action.component';
import { BlockContactFormComponent } from '@core-app/blocks/block-contact-form/block-contact-form.component';
import { CoreChatModule } from '@gallaghercp/core-chat';
import { BlockHomeBannerComponent } from '@core-app/blocks/block-home-banner/block-home-banner.component';

// Custom Modules & Services
import { BlockService, DemoConfigModule, PageModule, SpinnerModule, UtilsModule } from '@gallaghercp/core-frontend';
import { SwiperModule } from '@gallaghercp/core-swiper';
import { TranslateModule } from '@ngx-translate/core';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { CollapseModule } from 'ngx-bootstrap/collapse';

// Bootstrap Modules
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ClientModule } from '../client/client.module';
import { BackToTopComponent } from './back-to-top/back-to-top.component';
import { DarkModeToggleComponent } from './dark-mode-toggle/dark-mode-toggle.component';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { LoginFaqsComponent } from './login-faqs/login-faqs.component';
import { LoginFooterComponent } from './login-footer/login-footer.component';
import { ActivateComponent } from './login/activate.component';
import { LoginComponent } from './login/login.component';

// App Components
import { MainComponent } from './main/main.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { SideNavFooterComponent } from './nav/side-nav/side-nav-footer/side-nav-footer.component';
import { SideNavComponent } from './nav/side-nav/side-nav.component';
import { PostThumbnailsComponent } from './nav/top-nav/post-thumbnails/post-thumbnails.component';
import { TopNavComponent } from './nav/top-nav/top-nav.component';
import { NotificationsComponent } from './notifications/notifications.component';
import { OopsComponent } from './oops/oops.component';
import { PostComponent } from './post/post.component';
import { RegisterComponent } from './register/register.component';
import { ResetConfirmComponent } from './reset/reset-confirm.component';
import { ResetRequestComponent } from './reset/reset-request.component';
import { BlockDownloadsBucketComponent } from '@core-app/blocks/block-downloads-bucket/block-downloads-bucket.component';
import { BlockAccordionComponent } from '@core-app/blocks/block-accordion/block-accordion.component';

@NgModule({
    declarations: [
        MainComponent,
        MaintenanceComponent,
        OopsComponent,
        HeaderComponent,
        FooterComponent,
        SideNavComponent,
        SideNavFooterComponent,
        TopNavComponent,
        LoginComponent,
        RegisterComponent,
        BlockProfileComponent,
        LoginFooterComponent,
        ResetRequestComponent,
        ResetConfirmComponent,
        LoginFaqsComponent,
        ActivateComponent,
        BackToTopComponent,
        NotificationsComponent,
        PostComponent,
        PostThumbnailsComponent,
        DarkModeToggleComponent,
        BlockChatComponent,
        BlockCallToActionComponent,
        BlockLogoSliderComponent,
        BlockContactFormComponent,
        BlockHomeBannerComponent,
        BlockDownloadsBucketComponent,
        BlockAccordionComponent,
    ],
    imports: [
        CommonModule,
        RouterModule,
        BsDropdownModule,
        CollapseModule,
        ModalModule,
        ButtonsModule,
        CarouselModule,
        TabsModule,
        FormsModule,
        ReactiveFormsModule,
        MatProgressBarModule,
        SpinnerModule,
        AlertModule,
        TranslateModule,
        MatTabsModule,
        PageModule,
        MatExpansionModule,
        UtilsModule,
        TooltipModule,
        ClientModule,
        SwiperModule,
        CoreChatModule,
        DemoConfigModule,
        InfiniteScrollModule,
        NgOptimizedImage,
    ],
    exports: [
        MainComponent,
        CommonModule,
        HeaderComponent,
        NotificationsComponent,
        FooterComponent,
        BackToTopComponent,
        DarkModeToggleComponent,
        LoginFooterComponent,
        SideNavComponent,
        PostThumbnailsComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LayoutModule {
    protected blocks = inject(BlockService);

    constructor() {
        this.blocks.addBlock('CTA', BlockCallToActionComponent);
        this.blocks.addBlock('LOGO-SLIDER', BlockLogoSliderComponent);
        this.blocks.addBlock('CONTACT-FORM', BlockContactFormComponent);
        this.blocks.addBlock('HOME-BANNER', BlockHomeBannerComponent);
        this.blocks.addBlock('DOWNLOADS-BUCKET', BlockDownloadsBucketComponent);
        this.blocks.addBlock('ACCORDION', BlockAccordionComponent);
    }
}
